import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { DndContext, PointerSensor, useSensors, useSensor } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { makeSortableId, parseSortableId, removeFromArray, insertIntoArray } from "../utils/reorderCache";
import { moveComponent as moveComponentApi, moveItem as moveItemApi, reorderEntity } from "../utils/reorderMove";
import { collisionDetectionStrategy } from "../utils/collisionDetection";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import LoadingPage from "../../renderer/utils/LoadingPage";
import NotFoundWebsite from "../../renderer/pages/NotFoundWebsite";
import ErrorWebsite from "../../renderer/pages/ErrorWebsite";
import WebsitePage from "../../renderer/pages/WebsitePage";
import Website from "../../renderer/pages/Website";
import useContainerWidth from "../../core/hooks/useContainerWidth";
import EditNavBar from "../navbar/EditNavBar";
import { EditUIProvider, useEditUI } from "../context/EditUIPreferencesContext";
import ElementEditPanel from "../components/panels/ElementEditPanel";
import AIChatSection from "../components/ai/AIChatSection";
import { EditProvider, useEdit } from "../context/EditContext";
import { PreviewProvider, usePreview } from "../context/PreviewContext";
import { AIProvider } from "../context/AIContext";
import { EditorInteractionProvider, useEditorInteractionOptional } from "../context/EditorInteractionContext";
import { ExpandedFieldProvider } from "../context/ExpandedFieldContext";
import AddButtonWithIndicator from "../components/AddButtonWithIndicator";
import AddButtonOverlay from "../add-buttons/AddButtonOverlay";
import { AddButtonLayerProvider } from "../add-buttons/AddButtonLayerContext";
import { useAddElement } from "../context/AddElementContext";
// OLD: Floating AIChatPanel replaced by integrated AIChatSection in EditPanel
// import AIChatPanel from "./components/ai/AIChatPanel";
import Component from "../../renderer/components/Component";
import { AddElementProvider } from "../context/AddElementContext";
import { apiFetch } from "../../core/utils/api";
import { generateEditUrl } from "../../core/utils/urlUtils";
import "../device-frames.css";
import { Z_INDEX } from "../../core/utils/zIndexScale";
import PreviewBackground from "../components/PreviewBackground";
import { DOCK_LAYOUT_SPECS, clampAIPaneWidth, clampEditPaneWidth, EDIT_MIN_WIDTH_PX, AI_MIN_WIDTH_PX } from "../utils/dockLayout";
import { Modal, PrimaryButton, SecondaryButton } from "../../design-system";
import { ExternalLinkIcon, EyeIcon, PencilIcon } from "../../design-system/icons";
import { SparklesIcon } from "../navbar/icons";
import { APP_DOMAIN } from "../../core/utils/config";
import { typography, surfaces, borders, textColors, interactive } from "../../design-system/tokens";
import useDevRenderLoopGuard from "../../core/hooks/useDevRenderLoopGuard";
import { DEV_LOG_TITLE } from "../../core/utils/devLogTitle";
import { EditorTutorialOverlay, EditorTutorialProvider, useEditorTutorial } from "../tutorial";
import { TUTORIAL_EVENTS, TUTORIAL_TARGETS } from "../tutorial/constants";
import {
  WEBSITE_CREATION_SHOWCASE_DURATION_MS,
  WEBSITE_CREATION_SHOWCASE_EXIT_MS,
  WEBSITE_CREATION_SHOWCASE_FINAL_BLAST_MS,
  WEBSITE_CREATION_SHOWCASE_TOTAL_MS,
} from "../showcase/creationShowcaseTiming";
import SubscriptionPlansModal from "../../subscription/SubscriptionPlansModal";

const MOBILE_BREAKPOINT = 768; // px

const MovingStatusOverlay = React.memo(function MovingStatusOverlay({ label }) {
  if (!label) return null;

  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none flex items-center justify-center"
      style={{ zIndex: Z_INDEX.EDIT_OVERLAY }}
    >
      <div
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 shadow-sm ${surfaces.elevated} border ${borders.default} ${interactive.link}`}
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M12 3V9M12 3L9 6M12 3L15 6M12 15V21M12 21L15 18M12 21L9 18M3 12H9M3 12L6 15M3 12L6 9M15 12H21M21 12L18 9M21 12L18 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className={`${typography.button} font-bold leading-none`}>
          Moving {label}...
        </span>
      </div>
    </div>
  );
});

MovingStatusOverlay.displayName = "MovingStatusOverlay";

const WebsiteCreationShowcase = ({ isActive }) => {
  const reduceMotion = useReducedMotion();
  const durationSec = WEBSITE_CREATION_SHOWCASE_DURATION_MS / 1000;
  const exitSec = WEBSITE_CREATION_SHOWCASE_EXIT_MS / 1000;

  // A more dramatic final hit, timed to end right as the showcase completes.
  const finalBlastDurationSec = WEBSITE_CREATION_SHOWCASE_FINAL_BLAST_MS / 1000;
  const finalBlastDelaySec = Math.max(0, durationSec - finalBlastDurationSec);

  const sparkles = useMemo(
    () => [
      { id: "s1", left: "46%", top: "44%", size: 3, delay: 0.05 },
      { id: "s2", left: "53%", top: "40%", size: 4, delay: 0.12 },
      { id: "s3", left: "58%", top: "48%", size: 2, delay: 0.18 },
      { id: "s4", left: "41%", top: "50%", size: 2, delay: 0.22 },
      { id: "s5", left: "62%", top: "42%", size: 3, delay: 0.26 },
      { id: "s6", left: "49%", top: "55%", size: 4, delay: 0.32 },
      { id: "s7", left: "36%", top: "46%", size: 2, delay: 0.36 },
      { id: "s8", left: "66%", top: "52%", size: 2, delay: 0.42 },
    ],
    [],
  );

  return (
    <AnimatePresence>
      {isActive ? (
        <motion.div
          aria-hidden="true"
          className="fixed inset-0 pointer-events-none overflow-hidden"
          style={{ zIndex: Z_INDEX.POPOVER - 1 }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: exitSec, ease: "easeOut" }}
        >
          {/* Ambient background so the reveal reads clearly even while the website is hidden */}
          <motion.div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 45%, rgba(59,130,246,0.22) 0%, rgba(17,24,39,0.86) 60%, rgba(0,0,0,0.96) 100%)",
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.5, 0.65, 0.55] }}
            transition={{ duration: durationSec, ease: "easeInOut" }}
          />

          {/* Cinematic curtain (blur + dim) that fades away to "reveal" the editor */}
          <motion.div
            className="absolute inset-0"
            style={{
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(12px) saturate(1.15)",
              WebkitBackdropFilter: "blur(12px) saturate(1.15)",
            }}
            initial={{ opacity: 0.92 }}
            animate={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 1.0 : 2.0, ease: "easeOut" }}
          />

          {/* Subtle vignette for depth */}
          <motion.div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(circle at 50% 45%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 100%)",
            }}
            initial={{ opacity: 0.45 }}
            animate={{ opacity: 0 }}
            transition={{ duration: reduceMotion ? 1.0 : 2.2, ease: "easeOut" }}
          />

          {/* Light flash + ring + sparkles */}
          {!reduceMotion ? (
            <>
              <motion.div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.22) 0%, rgba(191,219,254,0.22) 28%, rgba(59,130,246,0) 62%)",
                  mixBlendMode: "screen",
                }}
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: [0, 1, 0], scale: [0.985, 1.02, 1.0] }}
                transition={{ duration: 1.6, ease: "easeInOut", times: [0, 0.28, 1] }}
              />

              <motion.div
                className="absolute left-1/2 top-[45%] -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  width: 260,
                  height: 260,
                  border: "1px solid rgba(191,219,254,0.55)",
                  boxShadow:
                    "0 0 0 0 rgba(59,130,246,0), 0 0 34px rgba(59,130,246,0.18), inset 0 0 18px rgba(255,255,255,0.12)",
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: [0, 0.7, 0], scale: [0.8, 1.05, 1.35] }}
                transition={{ duration: 2.0, ease: "easeOut", times: [0, 0.35, 1] }}
              />

              {sparkles.map((s) => (
                <motion.div
                  key={s.id}
                  className="absolute rounded-full"
                  style={{
                    left: s.left,
                    top: s.top,
                    width: s.size,
                    height: s.size,
                    background: "rgba(255,255,255,0.92)",
                    boxShadow: "0 0 14px rgba(147,197,253,0.65)",
                  }}
                  initial={{ opacity: 0, scale: 0.6, y: 0 }}
                  animate={{ opacity: [0, 1, 0], scale: [0.6, 1.25, 0.9], y: [0, -10, -22] }}
                  transition={{ duration: 1.45, delay: s.delay, ease: "easeOut", times: [0, 0.25, 1] }}
                />
              ))}

              {/* Final blast of light at the end */}
              <motion.div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle at 50% 45%, rgba(255,255,255,0.72) 0%, rgba(191,219,254,0.52) 42%, rgba(59,130,246,0) 82%)",
                  mixBlendMode: "screen",
                  filter: "blur(1px)",
                }}
                initial={{ opacity: 0, scale: 1 }}
                animate={{ opacity: [0, 1, 0], scale: [1.0, 1.14, 1.0] }}
                transition={{
                  delay: finalBlastDelaySec,
                  duration: finalBlastDurationSec,
                  ease: "easeInOut",
                  times: [0, 0.25, 1],
                }}
              />
            </>
          ) : (
            <motion.div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(circle at 50% 45%, rgba(191,219,254,0.18) 0%, rgba(59,130,246,0) 62%)",
                mixBlendMode: "screen",
              }}
              initial={{ opacity: 0.0 }}
              animate={{ opacity: [0, 0.35, 0] }}
              transition={{ duration: 1.25, ease: "easeInOut" }}
            />
          )}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

// Lightweight confetti overlay (mirrors dashboard style)
const ConfettiOverlay = ({ isActive, durationMs = 2500 }) => {
  const [pieces] = useState(() =>
    Array.from({ length: 50 }, (_, i) => {
      const colors = [
        "#86efac", // green-300
        "#4ade80", // green-400
        "#a7f3d0", // emerald-200
        "#93c5fd", // blue-300
        "#fcd34d", // amber-300
        "#fca5a5", // red-300
      ];
      const shapes = ["circle", "square", "triangle"];
      return {
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        shape: shapes[Math.floor(Math.random() * shapes.length)],
        left: Math.random() * 100,
        animationDelay: Math.random() * 1.5,
        animationDuration: 1.6 + Math.random() * 1.6,
        size: 4 + Math.random() * 8,
      };
    })
  );
  if (!isActive) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {pieces.map((p) => (
        <div
          key={p.id}
          className={`absolute animate-confetti-fall ${
            p.shape === "circle"
              ? "rounded-full"
              : p.shape === "triangle"
              ? "confetti-triangle"
              : ""
          }`}
          style={{
            left: `${p.left}%`,
            top: `-300px`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            backgroundColor: p.shape !== "triangle" ? p.color : "transparent",
            borderColor: p.shape === "triangle" ? p.color : "transparent",
            animationDelay: `${p.animationDelay}s`,
            animationDuration: `${p.animationDuration}s`,
            "--random-x": `${(Math.random() - 0.5) * 200}px`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-200px) translateX(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(calc(100vh + 100px)) translateX(var(--random-x)) rotate(720deg); opacity: 0; }
        }
        .animate-confetti-fall { animation: confetti-fall linear infinite; }
        .confetti-triangle {
          width: 0; height: 0;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 12px solid; 
        }
      `}</style>
    </div>
  );
};

const ZoomControl = () => {
  const { mode, viewport, manualZoom, setManualZoom } = usePreview();
  
  if (mode === "responsive" || !viewport) return null;
  
  return (
    <div className={`${surfaces.primary} border-b ${borders.default} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <span className={`${typography.bodySmall} font-medium ${textColors.primary}`}>Preview Zoom</span>
          <div className="flex items-center space-x-2">
            <span className={`${typography.caption} ${textColors.muted}`}>20%</span>
            <input
              type="range"
              min="0.2"
              max="2.0"
              step="0.1"
              value={manualZoom}
              onChange={(e) => setManualZoom(parseFloat(e.target.value))}
              className="w-24 h-2 bg-gray-200 dark:bg-dark-700 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((manualZoom - 0.2) / 1.8) * 100}%, #e5e7eb ${((manualZoom - 0.2) / 1.8) * 100}%, #e5e7eb 100%)`
              }}
            />
            <span className={`${typography.caption} ${textColors.muted}`}>200%</span>
          </div>
          <span className={`${typography.bodySmall} font-medium text-blue-600 dark:text-blue-400`}>{Math.round(manualZoom * 100)}%</span>
        </div>
        <div className={`${typography.caption} ${textColors.muted}`}>
          {viewport.width} × {viewport.height}px
        </div>
      </div>
    </div>
  );
};

const DeviceStage = React.memo(({ children, availableWidth, availableHeight }) => {
  const { mode, viewport, frameKind, showFrame, orientation, manualZoom, setContainerWidth } = usePreview();
  useDevRenderLoopGuard("DeviceStage");

  // Update containerWidth in context whenever the effective width changes
  useEffect(() => {
    const width = (!viewport || mode === "responsive") ? availableWidth : viewport.width;
    if (typeof width !== "number" || !Number.isFinite(width) || width <= 0) return;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.debug(`${DEV_LOG_TITLE} DeviceStage setContainerWidth`, {
        width,
        mode,
        viewportWidth: viewport?.width,
        availableWidth,
      });
    }
    setContainerWidth(width);
  }, [viewport, mode, availableWidth, setContainerWidth]);

  // If responsive mode, just render children as-is
  if (!viewport || mode === "responsive") {
    return (
      <PreviewBackground className="h-full overflow-y-auto" onScroll={(e) => e.stopPropagation()}>
        {children}
      </PreviewBackground>
    );
  }

  // Compute frame padding approximation (outer chrome thickness)
  const pad = (() => {
    switch (frameKind) {
      case "phone":
        return { x: 32, y: 32 };
      case "tablet":
        return { x: 24, y: 24 };
      case "laptop":
        return { x: 16, y: 28 };
      case "desktop":
        return { x: 20, y: 36 };
      default:
        return { x: 0, y: 0 };
    }
  })();

  const frameWidth = viewport.width + (showFrame ? pad.x : 0);
  const frameHeight = viewport.height + (showFrame ? pad.y : 0);
  const scale = manualZoom;

  return (
    <div className={`device-stage ${frameKind === "phone" ? "stage-phone" : ""}`} style={{ overflow: "auto" }}>
      {frameKind === "phone" && (
        <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div style={{ position: "absolute", width: 500, height: 900, borderRadius: 80, background: "radial-gradient(closest-side, rgba(0,0,0,0.08), transparent)", left: "50%", top: "50%", transform: "translate(-50%,-50%)" }} />
        </div>
      )}
      <div
        className={`device-frame ${showFrame ? frameKind : ""}`}
        style={{ transform: `scale(${scale})`, willChange: "transform" }}
      >
        <div
          className="device-viewport"
          style={{ width: viewport.width, height: viewport.height }}
        >
          {/* Phone chrome */}
          {showFrame && frameKind === "phone" && (
            <>
              <div className="phone-notch" />
              <div className="phone-speaker" />
              <div className="phone-camera" />
              <div className="phone-home-indicator" />
            </>
          )}
          {/* Inner scroll container so content taller than the fixed device height
              can be scrolled (the viewport itself is overflow:hidden). */}
          <div className="device-viewport-scroll">
            {/* Website content - containerWidth now from context */}
            {children}
          </div>
        </div>
        {/* Desktop stand */}
        {showFrame && frameKind === "desktop" && <div className="desktop-stand" />}
      </div>
    </div>
  );
});

/**
 * IMPORTANT:
 * This scaffold MUST be module-scoped (not defined inside `EditWebsitePage`) so its component identity
 * is stable across `EditWebsitePage` re-renders. Defining it inline causes React to treat it as a new
 * component type on every render, which unmounts/remounts the entire preview tree (looks like a reload)
 * and breaks drag-and-drop (dnd-kit contexts reset mid-gesture).
 */
const EditWebsiteScaffold = React.memo(function EditWebsiteScaffold({
  isMobile,
  showWebsite,
  setShowWebsite,
  toggleView,
  website,
  currentPage,
  handleWebsiteScroll,
  handleEditPanelScroll,
  hideWebsiteUntilShowcaseComplete,
  dragSensors,
  onDragStart,
  onDragEnd,
  activeDragMeta,
  setActiveDragMeta,
  preventNativeCanvasDragDrop,
  preventCanvasNavigation,
  version_slug,
}) {
  const editorInteraction = useEditorInteractionOptional();
  const { triggerAddMode, selectedElement, isAddMode, addModeTarget } = useEdit();
  const { isOpen: tutorialOpen, currentStep: tutorialStep } = useEditorTutorial();
  const [websiteRef] = useContainerWidth();

  // Stage measurement for zoom-to-fit calculations.
  // Keep this local to the editor scaffold so dock animations/ResizeObserver updates
  // don't force `EditWebsitePage` to re-render (which can amplify remount loops).
  const stageHostRef = useRef(null);
  const [stageDims, setStageDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = stageHostRef.current;
    if (!el) return;
    let rafId = null;
    const measure = () => {
      rafId = null;
      const rect = el.getBoundingClientRect();
      const nextW = Number.isFinite(rect.width) ? Math.round(rect.width) : 0;
      const nextH = Number.isFinite(rect.height) ? Math.round(rect.height) : 0;
      setStageDims((prev) => (prev.w === nextW && prev.h === nextH ? prev : { w: nextW, h: nextH }));
    };
    const schedule = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(measure);
    };
    schedule();
    const obs = new ResizeObserver(schedule);
    obs.observe(el);
    window.addEventListener("resize", schedule);
    return () => {
      try {
        obs.disconnect();
      } catch (_) {}
      window.removeEventListener("resize", schedule);
      if (rafId != null) {
        try { window.cancelAnimationFrame(rafId); } catch (_) {}
      }
    };
  }, []);

  const handleDndDragStart = useCallback(
    (evt) => {
      try {
        editorInteraction?.setDragging?.(true);
      } catch (_) {}
      onDragStart?.(evt);
    },
    [editorInteraction, onDragStart],
  );

  const handleDndDragCancel = useCallback(() => {
    try {
      editorInteraction?.setDragging?.(false);
    } catch (_) {}
    setActiveDragMeta?.(null);
  }, [editorInteraction, setActiveDragMeta]);

  const handleDndDragEnd = useCallback(
    async (evt) => {
      try {
        await onDragEnd?.(evt);
      } finally {
        try {
          editorInteraction?.setDragging?.(false);
        } catch (_) {}
      }
    },
    [editorInteraction, onDragEnd],
  );

  const {
    dockLayout,
    editPanelView,
    leftSidebarOpen,
    setLeftSidebarOpen,
    rightSidebarOpen,
    setRightSidebarOpen,
    leftSidebarWidthPx,
    setLeftSidebarWidthPx,
    rightSidebarWidthPx,
    setRightSidebarWidthPx,
    isTablet,
  } = useEditUI();

  const [leftSplitRatio, setLeftSplitRatio] = useState(0.5);
  const [rightSplitRatio, setRightSplitRatio] = useState(0.5);
  const [aiCollapsedLeft, setAiCollapsedLeft] = useState(false);
  const [aiCollapsedRight, setAiCollapsedRight] = useState(false);
  const [suppressAutoOpenForSelectionId, setSuppressAutoOpenForSelectionId] = useState(null);
  const [mobilePanelKind, setMobilePanelKind] = useState("edit"); // 'edit' | 'ai'

  const tutorialTargetId = tutorialStep?.targetId || null;
  const tutorialMobileView = useMemo(() => {
    if (!tutorialOpen) return null;
    if (tutorialTargetId === TUTORIAL_TARGETS.CANVAS) return "preview";
    if (tutorialTargetId === TUTORIAL_TARGETS.ADD_BUTTON) return "preview";
    if (tutorialTargetId === TUTORIAL_TARGETS.EDIT_PANEL) return "edit";
    if (tutorialTargetId === TUTORIAL_TARGETS.EDIT_PANEL_TABS) return "edit";
    if (tutorialTargetId === TUTORIAL_TARGETS.ADD_ELEMENT_PANEL) return "edit";
    if (tutorialTargetId === TUTORIAL_TARGETS.AI_CHAT) return "ai";
    return null;
  }, [tutorialOpen, tutorialTargetId]);

  const layoutSpec = DOCK_LAYOUT_SPECS[dockLayout] || {};
  const leftPanes = layoutSpec.sides?.left || null; // 'ai' | 'edit' | [..] | null
  const rightPanes = layoutSpec.sides?.right || null;
  const hasLeft = Boolean(leftPanes);
  const hasRight = Boolean(rightPanes);
  const leftPaneList = useMemo(() => (Array.isArray(leftPanes) ? leftPanes : leftPanes ? [leftPanes] : []), [leftPanes]);
  const rightPaneList = useMemo(() => (Array.isArray(rightPanes) ? rightPanes : rightPanes ? [rightPanes] : []), [rightPanes]);
  const leftPrimaryKind = useMemo(() => (leftPaneList.length > 0 ? leftPaneList[0] : null), [leftPaneList]);
  const rightPrimaryKind = useMemo(() => (rightPaneList.length > 0 ? rightPaneList[0] : null), [rightPaneList]);

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  const clampForSide = useCallback((panes) => {
    const panesArr = Array.isArray(panes) ? panes : [panes].filter(Boolean);
    if (panesArr.includes('edit')) return (w) => clampEditPaneWidth(w, viewportWidth);
    return (w) => clampAIPaneWidth(w, viewportWidth);
  }, [viewportWidth]);

  useEffect(() => {
    const currentId = selectedElement?.id ?? null;
    if (suppressAutoOpenForSelectionId && suppressAutoOpenForSelectionId !== currentId) {
      setSuppressAutoOpenForSelectionId(null);
    }
  }, [selectedElement?.id, suppressAutoOpenForSelectionId]);

  // Persist inner splitter ratio per session and per side/layout
  const leftSplitKey = useMemo(() => `dock:split:left:${dockLayout}`, [dockLayout]);
  const rightSplitKey = useMemo(() => `dock:split:right:${dockLayout}`, [dockLayout]);

  useEffect(() => {
    try {
      const v = parseFloat(sessionStorage.getItem(leftSplitKey));
      if (!Number.isNaN(v) && v > 0 && v < 1) setLeftSplitRatio(v);
    } catch (_) {}
  }, [leftSplitKey]);

  useEffect(() => {
    try {
      const v = parseFloat(sessionStorage.getItem(rightSplitKey));
      if (!Number.isNaN(v) && v > 0 && v < 1) setRightSplitRatio(v);
    } catch (_) {}
  }, [rightSplitKey]);

  useEffect(() => {
    try { sessionStorage.setItem(leftSplitKey, String(leftSplitRatio)); } catch (_) {}
  }, [leftSplitKey, leftSplitRatio]);

  useEffect(() => {
    try { sessionStorage.setItem(rightSplitKey, String(rightSplitRatio)); } catch (_) {}
  }, [rightSplitKey, rightSplitRatio]);

  const startResize = useCallback((side) => {
    const container = document.getElementById('edit-container');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const isLeft = side === 'left';
    const clamp = clampForSide(isLeft ? leftPanes : rightPanes);

    const onMove = (e) => {
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      if (clientX == null) return;
      let newWidth = isLeft ? (clientX - rect.left) : (rect.right - clientX);
      if (newWidth <= 16) {
        if (isLeft) setLeftSidebarOpen(false); else setRightSidebarOpen(false);
        return;
      }
      newWidth = clamp(newWidth);
      if (isLeft) setLeftSidebarWidthPx(newWidth); else setRightSidebarWidthPx(newWidth);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [clampForSide, leftPanes, rightPanes, setLeftSidebarOpen, setRightSidebarOpen, setLeftSidebarWidthPx, setRightSidebarWidthPx]);

  const startInnerSplitResize = useCallback((side) => {
    const dockEl = document.querySelector(`[data-dock="${side}"]`);
    if (!dockEl) return;
    const rect = dockEl.getBoundingClientRect();
    const onMove = (e) => {
      const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
      if (clientY == null) return;
      const ratio = (clientY - rect.top) / rect.height;
      const clamped = Math.max(0.15, Math.min(0.85, ratio));
      if (side === 'left') setLeftSplitRatio(clamped); else setRightSplitRatio(clamped);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, []);

  // Keyboard handlers for accessibility
  const handleOuterResizerKeyDown = useCallback((e, side) => {
    const isLeft = side === 'left';
    const clamp = clampForSide(isLeft ? leftPanes : rightPanes);
    if (e.key === 'Enter') {
      if (isLeft) {
        const willOpen = !leftSidebarOpen;
        setLeftSidebarOpen(willOpen);
        if (!willOpen && leftPaneList.includes('edit') && selectedElement?.id) setSuppressAutoOpenForSelectionId(selectedElement.id);
      } else {
        const willOpen = !rightSidebarOpen;
        setRightSidebarOpen(willOpen);
        if (!willOpen && rightPaneList.includes('edit') && selectedElement?.id) setSuppressAutoOpenForSelectionId(selectedElement.id);
      }
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      const step = e.shiftKey ? 24 : 8;
      const direction = (() => {
        if (isLeft) return e.key === 'ArrowLeft' ? -1 : 1;
        return e.key === 'ArrowLeft' ? 1 : -1;
      })();
      const currentWidth = isLeft ? leftSidebarWidthPx : rightSidebarWidthPx;
      let nextWidth = clamp(currentWidth + direction * step);
      if (nextWidth <= 16) {
        if (isLeft) setLeftSidebarOpen(false); else setRightSidebarOpen(false);
      } else {
        if (isLeft) setLeftSidebarWidthPx(nextWidth); else setRightSidebarWidthPx(nextWidth);
      }
      e.preventDefault();
    }
  }, [clampForSide, leftPanes, rightPanes, leftSidebarWidthPx, rightSidebarWidthPx, leftSidebarOpen, rightSidebarOpen, setLeftSidebarOpen, setRightSidebarOpen, setLeftSidebarWidthPx, setRightSidebarWidthPx, leftPaneList, rightPaneList, selectedElement?.id]);

  const handleInnerResizerKeyDown = useCallback((e, side) => {
    const step = e.shiftKey ? 0.18 : 0.08;
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (side === 'left') {
        const next = Math.max(0.15, Math.min(0.85, leftSplitRatio + (e.key === 'ArrowDown' ? step : -step)));
        setLeftSplitRatio(next);
      } else {
        const next = Math.max(0.15, Math.min(0.85, rightSplitRatio + (e.key === 'ArrowDown' ? step : -step)));
        setRightSplitRatio(next);
      }
      e.preventDefault();
    }
  }, [leftSplitRatio, rightSplitRatio]);

  const isLeftExpanded = leftSidebarWidthPx >= viewportWidth * 0.5;
  const isRightExpanded = rightSidebarWidthPx >= viewportWidth * 0.5;

  const collapseSide = useCallback((side) => {
    const isLeft = side === 'left';
    const currentWidth = isLeft ? leftSidebarWidthPx : rightSidebarWidthPx;
    const isExpanded = currentWidth >= viewportWidth * 0.5;

    if (isExpanded) {
      const thirtyPct = Math.round(viewportWidth * 0.3);
      if (isLeft) {
        setLeftSidebarWidthPx(clampEditPaneWidth(thirtyPct, viewportWidth));
      } else {
        setRightSidebarWidthPx(clampEditPaneWidth(thirtyPct, viewportWidth));
      }
    } else {
      if (isLeft) {
        setLeftSidebarOpen(false);
      } else {
        setRightSidebarOpen(false);
      }
      const paneList = isLeft ? leftPaneList : rightPaneList;
      if (paneList.includes('edit') && selectedElement?.id) {
        setSuppressAutoOpenForSelectionId(selectedElement.id);
      }
    }
  }, [leftPaneList, rightPaneList, selectedElement?.id, setLeftSidebarOpen, setRightSidebarOpen, leftSidebarWidthPx, rightSidebarWidthPx, viewportWidth, setLeftSidebarWidthPx, setRightSidebarWidthPx]);

  const expandSide = useCallback((side) => {
    const halfVw = Math.round(viewportWidth * 0.5);
    const isLeft = side === 'left';
    if (isLeft) {
      setLeftSidebarWidthPx(clampEditPaneWidth(halfVw, viewportWidth));
      if (hasRight) {
        setRightSidebarOpen(false);
      }
    } else {
      setRightSidebarWidthPx(clampAIPaneWidth(halfVw, viewportWidth));
      if (hasLeft) {
        setLeftSidebarOpen(false);
      }
    }
  }, [viewportWidth, hasLeft, hasRight, setLeftSidebarWidthPx, setRightSidebarWidthPx, setLeftSidebarOpen, setRightSidebarOpen]);

  const renderPaneForSide = useCallback((kind, side) => {
    if (kind === 'ai') {
      const collapsed = side === 'left' ? aiCollapsedLeft : aiCollapsedRight;
      const onToggle = () => (side === 'left' ? setAiCollapsedLeft((v) => !v) : setAiCollapsedRight((v) => !v));
      return (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <AIChatSection collapsed={collapsed} onToggle={onToggle} websiteId={website?.id} fillParent={true} />
        </div>
      );
    }
    if (kind === 'edit') {
      return (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto" onScroll={handleEditPanelScroll}>
            <ElementEditPanel />
          </div>
        </div>
      );
    }
    return null;
  }, [website?.id, handleEditPanelScroll, aiCollapsedLeft, aiCollapsedRight]);

  const bridgedOpenAddModal = (target, event) => {
    console.log('🔍 ADDBUTTON_ORDER_BUG - bridgedOpenAddModal called:', {
      target,
      hasOrder: typeof target?.order === 'number',
      orderValue: target?.order,
    });
    // Tutorial integration: Step "add_button" advances when the user clicks a real canvas + button.
    try {
      window.dispatchEvent(
        new CustomEvent(TUTORIAL_EVENTS.ADD_BUTTON_CLICKED, {
          detail: { source: "canvas-add-button", target, ts: Date.now() },
        }),
      );
    } catch (_) {}
    // Call triggerAddMode to open AddElementPanel in the sidebar
    triggerAddMode(target);
    // On mobile, ensure we switch to the edit panel so the user sees AddElementPanel
    if (isMobile) {
      setMobilePanelKind("edit");
      setShowWebsite?.(false);
    }
  };

  const tutorialFallbackAddTarget = useMemo(() => {
    if (!currentPage?.id) return null;
    return {
      pageId: currentPage.id,
      websiteId: website?.id,
      order: 0,
    };
  }, [currentPage?.id, website?.id]);

  useEffect(() => {
    const handleTutorialOpenAddElementPanel = (evt) => {
      const requestedTarget =
        evt?.detail?.target && typeof evt.detail.target === "object"
          ? evt.detail.target
          : null;
      const target = requestedTarget || tutorialFallbackAddTarget;
      if (!target) return;

      const hasValidParent =
        target.pageId !== undefined ||
        target.componentId !== undefined ||
        target.heroId !== undefined ||
        target.buttonCollectionId !== undefined;
      if (!hasValidParent) return;

      // Don't interrupt if the add panel is already open.
      if (isAddMode && addModeTarget) return;

      triggerAddMode(target);

      // Ensure the edit pane is visible so the user can immediately see AddElementPanel.
      if (isMobile) {
        setMobilePanelKind("edit");
        setShowWebsite?.(false);
        return;
      }

      const leftHasEdit = leftPaneList.includes("edit");
      const rightHasEdit = rightPaneList.includes("edit");
      if (leftHasEdit && !leftSidebarOpen) setLeftSidebarOpen(true);
      if (rightHasEdit && !rightSidebarOpen) setRightSidebarOpen(true);
    };

    window.addEventListener(TUTORIAL_EVENTS.OPEN_ADD_ELEMENT_PANEL, handleTutorialOpenAddElementPanel);
    return () => {
      window.removeEventListener(TUTORIAL_EVENTS.OPEN_ADD_ELEMENT_PANEL, handleTutorialOpenAddElementPanel);
    };
  }, [
    tutorialFallbackAddTarget,
    isAddMode,
    addModeTarget,
    triggerAddMode,
    leftPaneList,
    rightPaneList,
    leftSidebarOpen,
    rightSidebarOpen,
    setLeftSidebarOpen,
    setRightSidebarOpen,
    isMobile,
    setShowWebsite,
  ]);

  // Auto-open edit dock when an element is selected
  useEffect(() => {
    if (!selectedElement) return;
    if (suppressAutoOpenForSelectionId && suppressAutoOpenForSelectionId === selectedElement.id) return;
    if (isMobile) {
      setMobilePanelKind("edit");
      setShowWebsite?.(false);
      return;
    }
    const leftHasEdit = leftPaneList.includes('edit');
    const rightHasEdit = rightPaneList.includes('edit');
    if (leftHasEdit && !leftSidebarOpen) setLeftSidebarOpen(true);
    if (rightHasEdit && !rightSidebarOpen) setRightSidebarOpen(true);
  }, [
    selectedElement,
    suppressAutoOpenForSelectionId,
    leftPaneList,
    rightPaneList,
    leftSidebarOpen,
    rightSidebarOpen,
    setLeftSidebarOpen,
    setRightSidebarOpen,
    isMobile,
    setShowWebsite,
  ]);

  // Auto-switch to edit panel on mobile when animations panel opens
  useEffect(() => {
    if (!isMobile) return;
    if (editPanelView === "animations") {
      setMobilePanelKind("edit");
      setShowWebsite?.(false);
    }
  }, [editPanelView, isMobile, setShowWebsite]);

  // Switch to preview when element selection is cleared on mobile (e.g. X button in edit panel)
  const prevSelectedElementRef = useRef(selectedElement);
  useEffect(() => {
    const wasSelected = !!prevSelectedElementRef.current;
    prevSelectedElementRef.current = selectedElement;

    if (!isMobile) return;
    // Only switch to preview when going from selected → unselected and not entering add mode
    if (wasSelected && !selectedElement && !isAddMode) {
      setShowWebsite?.(true);
    }
  }, [selectedElement, isAddMode, isMobile, setShowWebsite]);

  // Switch to preview when the add-element panel is explicitly dismissed on mobile
  // (user clicks X or presses Escape). ElementEditPanel dispatches this event only
  // from the cancel path — NOT from the element-creation path — so there is no
  // false-positive flash when a new element is being created.
  useEffect(() => {
    if (!isMobile) return;
    const handlePanelDismissed = () => setShowWebsite?.(true);
    window.addEventListener("editor:panel-dismissed", handlePanelDismissed);
    return () => window.removeEventListener("editor:panel-dismissed", handlePanelDismissed);
  }, [isMobile, setShowWebsite]);

  // Tutorial: ensure the correct mobile view is visible for the active step target.
  // This keeps the coachmark anchored to real DOM nodes and prevents "missing target"
  // situations when the user is on the wrong mobile panel.
  useEffect(() => {
    if (!isMobile) return;
    if (!tutorialOpen) return;
    if (!tutorialMobileView) return;

    if (tutorialMobileView === "preview") {
      if (!showWebsite) setShowWebsite?.(true);
      return;
    }

    if (showWebsite) setShowWebsite?.(false);

    if (tutorialMobileView === "ai" && mobilePanelKind !== "ai") {
      setMobilePanelKind("ai");
    } else if (tutorialMobileView === "edit" && mobilePanelKind !== "edit") {
      setMobilePanelKind("edit");
    }
  }, [
    isMobile,
    tutorialOpen,
    tutorialMobileView,
    showWebsite,
    mobilePanelKind,
    setShowWebsite,
    setMobilePanelKind,
  ]);

  const navOffsetPx = isTablet ? 48 : 64; // tablet: h-12, desktop: h-16
  const MOBILE_VIEW_BAR_HEIGHT_PX = 48;
  const containerTopPx = navOffsetPx + (isMobile ? MOBILE_VIEW_BAR_HEIGHT_PX : 0);

  const mobileViewBar = isMobile ? (
    <div
      className="sticky z-20 bg-gradient-to-r from-blue-600 to-blue-800 dark:from-dark-950 dark:to-dark-900 text-white border-b border-white/10 dark:border-dark-700"
      style={{ top: navOffsetPx }}
    >
      <div className="h-12 px-3 flex items-center">
        <div className="w-full grid grid-cols-3 rounded-lg overflow-hidden border border-white/20 dark:border-dark-600">
          <button
            type="button"
            onClick={() => setShowWebsite?.(true)}
            aria-pressed={showWebsite}
            className={[
              "h-9 px-2",
              "inline-flex items-center justify-center gap-1",
              "whitespace-nowrap text-xs font-medium",
              showWebsite ? "bg-white/20 text-white" : "bg-transparent text-white/90 hover:bg-white/10",
            ].join(" ")}
          >
            <EyeIcon size="xs" color="white" />
            Preview
          </button>
          <button
            type="button"
            onClick={() => {
              setMobilePanelKind("edit");
              setShowWebsite?.(false);
            }}
            aria-pressed={!showWebsite && mobilePanelKind === "edit"}
            className={[
              "h-9 px-2",
              "inline-flex items-center justify-center gap-1",
              "whitespace-nowrap text-xs font-medium",
              !showWebsite && mobilePanelKind === "edit"
                ? "bg-white/20 text-white"
                : "bg-transparent text-white/90 hover:bg-white/10",
            ].join(" ")}
          >
            <PencilIcon size="xs" color="white" />
            Edit
          </button>
          <button
            type="button"
            onClick={() => {
              setMobilePanelKind("ai");
              setShowWebsite?.(false);
            }}
            aria-pressed={!showWebsite && mobilePanelKind === "ai"}
            className={[
              "h-9 px-2",
              "inline-flex items-center justify-center gap-1",
              "whitespace-nowrap text-xs font-medium",
              !showWebsite && mobilePanelKind === "ai"
                ? "bg-white/20 text-white"
                : "bg-transparent text-white/90 hover:bg-white/10",
            ].join(" ")}
          >
            <SparklesIcon className="w-3 h-3" />
            AI
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <AddElementProvider openAddModal={bridgedOpenAddModal}>
      <div className={`min-h-screen flex flex-col ${surfaces.modernApp}`}>
        {mobileViewBar}

        <div
          id="edit-container"
          className="flex flex-row w-full relative sticky"
          style={{
            height: `calc(100vh - ${containerTopPx}px)`,
            top: containerTopPx,
          }}
        >
          {isMobile ? (
            <div className="h-full w-full">
              {showWebsite ? (
                <div ref={websiteRef} className="h-full w-full">
                  <PreviewBackground
                    data-tutorial-id="editor-canvas"
                    className="h-full flex flex-col overflow-hidden"
                    onScroll={handleWebsiteScroll}
                  >
                    <ZoomControl />
                    <div ref={stageHostRef} className="relative flex-1 min-h-0 overflow-hidden">
                      <DeviceStage availableWidth={stageDims.w} availableHeight={stageDims.h}>
                        <DndContext
                          sensors={dragSensors}
                          collisionDetection={collisionDetectionStrategy}
                          onDragStart={handleDndDragStart}
                          onDragCancel={handleDndDragCancel}
                          onDragEnd={handleDndDragEnd}
                        >
                          <motion.div
                            initial={false}
                            animate={{ opacity: hideWebsiteUntilShowcaseComplete ? 0 : 1 }}
                            transition={{
                              duration: hideWebsiteUntilShowcaseComplete ? 0 : 0.35,
                              ease: "easeOut",
                            }}
                            style={{
                              pointerEvents: hideWebsiteUntilShowcaseComplete ? "none" : "auto",
                              visibility: hideWebsiteUntilShowcaseComplete ? "hidden" : "visible",
                            }}
                            aria-hidden={hideWebsiteUntilShowcaseComplete ? "true" : undefined}
                            onDragStartCapture={preventNativeCanvasDragDrop}
                            onDragOverCapture={preventNativeCanvasDragDrop}
                            onDropCapture={preventNativeCanvasDragDrop}
                            onClickCapture={preventCanvasNavigation}
                            onSubmitCapture={preventCanvasNavigation}
                          >
                            <Website version_slug={version_slug} />
                          </motion.div>
                        </DndContext>
                      </DeviceStage>
                      <MovingStatusOverlay label={activeDragMeta?.label} />
                    </div>
                  </PreviewBackground>
                </div>
              ) : (
                <div className={`h-full w-full ${surfaces.primary} flex flex-col overflow-hidden`}>
                  {mobilePanelKind === "ai" ? renderPaneForSide("ai", "right") : renderPaneForSide("edit", "left")}
                </div>
              )}
            </div>
          ) : (
            <>
          {/* Left Dock */}
          {hasLeft ? (
            <AnimatePresence initial={false} mode="wait">
              {leftSidebarOpen ? (
                  <motion.div
                    key="dock-left-open"
                    id="dock-left"
                    data-dock="left"
                    data-testid="dock-left"
                    aria-expanded={leftSidebarOpen}
                    className={`h-full ${surfaces.glassCard} border-r ${borders.glass} shadow-lg relative flex flex-col overflow-hidden`}
                    initial={{ width: 0, opacity: 0, x: -8 }}
                  animate={{ width: leftSidebarWidthPx, opacity: 1, x: 0 }}
                  exit={{ width: 0, opacity: 0, x: -8 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  {Array.isArray(leftPanes) ? (
                    <div className="flex-1 min-h-0 flex flex-col">
                      <div className="relative" style={{ height: `${leftSplitRatio * 100}%` }}>
                        {renderPaneForSide(leftPanes[0], 'left')}
                      </div>
                      <div
                        data-dock-inner-resizer="left"
                        data-testid="dock-inner-resizer-left"
                        className={`relative h-6 bg-transparent ${surfaces.hover} cursor-ns-resize flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                        style={{ zIndex: Z_INDEX.DIVIDER }}
                        onPointerDown={() => startInnerSplitResize('left')}
                        onKeyDown={(e) => handleInnerResizerKeyDown(e, 'left')}
                        tabIndex={0}
                        aria-label="Resize left dock panes"
                        role="separator"
                        aria-orientation="horizontal"
                        aria-controls="dock-left"
                        title="Resize panes (Up/Down arrows; Shift for larger step)"
                      >
                        <div aria-hidden="true" className={`absolute inset-x-0 top-1/2 -translate-y-1/2 h-px ${borders.strong}`} />
                      </div>
                      <div className="flex-1 min-h-0">
                        {renderPaneForSide(leftPanes[1], 'left')}
                      </div>
                    </div>
                  ) : (
                    renderPaneForSide(leftPanes, 'left')
                  )}
                  <div
                    data-dock-resizer="left"
                    data-testid="dock-resizer-left"
                    className={`group absolute top-0 right-0 h-full w-6 cursor-col-resize focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${surfaces.hover}`}
                    style={{ zIndex: Z_INDEX.DIVIDER }}
                    onPointerDown={() => startResize('left')}
                    onDoubleClick={() => collapseSide('left')}
                    onKeyDown={(e) => handleOuterResizerKeyDown(e, 'left')}
                    tabIndex={0}
                    aria-label="Resize left dock"
                    aria-controls="dock-left"
                    role="separator"
                    aria-orientation="vertical"
                    title="Resize (Left/Right arrows; Enter to toggle; Double-click to collapse)"
                  >
                    {/* Visual grip */}
                    <div aria-hidden="true" className="absolute inset-y-0 right-0 w-6 flex items-center justify-center">
                      <div className={`h-10 w-1.5 rounded-full bg-gray-300 dark:bg-dark-600 group-hover:bg-gray-400 dark:group-hover:bg-dark-500 shadow-inner`} />
                    </div>
                    {/* Quick expand toggle button — only when below half viewport */}
                    {!isLeftExpanded && (
                      <button
                        type="button"
                        data-dock-expand="left"
                        className={`absolute top-1/2 -translate-y-1/2 right-7 -mt-4 w-6 h-6 rounded-full ${surfaces.elevated} border ${borders.default} shadow-sm ${surfaces.hover} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                        title="Expand left panel to half page"
                        aria-label="Expand left panel to half page"
                        onClick={(e) => { e.stopPropagation(); expandSide('left'); }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-4 h-4 ${textColors.tertiary} mx-auto`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6l6 6-6 6" />
                        </svg>
                      </button>
                    )}
                    {/* Quick collapse / shrink toggle button */}
                    <button
                      type="button"
                      data-dock-toggle="left"
                      className={`absolute top-1/2 -translate-y-1/2 right-7 ${isLeftExpanded ? '' : 'mt-4'} w-6 h-6 rounded-full ${surfaces.elevated} border ${borders.default} shadow-sm ${surfaces.hover} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                      title={isLeftExpanded ? "Shrink left panel" : "Collapse left panel"}
                      aria-label={isLeftExpanded ? "Shrink left panel" : "Collapse left panel"}
                      onClick={(e) => { e.stopPropagation(); collapseSide('left'); }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-4 h-4 ${textColors.tertiary} mx-auto`}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 6l-6 6 6 6" />
                      </svg>
                    </button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="dock-left-closed"
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="pointer-events-none"
                >
                  <div
                    className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col gap-2"
                    style={{ zIndex: Z_INDEX.DIVIDER }}
                  >
                    {/* Unified circle with icon/text */}
                    <button
                      data-dock-handle="left"
                      data-testid="dock-handle-left-open"
                      type="button"
                      className={`pointer-events-auto w-12 h-12 rounded-full ${surfaces.elevated} border ${borders.default} shadow ${surfaces.hover} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex items-center justify-center group`}
                      title="Open panel"
                      aria-label="Open panel"
                      onClick={() => setLeftSidebarOpen(true)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setLeftSidebarOpen(true); e.preventDefault(); } }}
                    >
                      <div className={`relative w-full h-full flex items-center justify-center ${textColors.secondary}`}>
                        {leftPrimaryKind === 'edit' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.121 2.121 0 113 3L8.5 17.85 4 19l1.15-4.5L16.862 3.487z" />
                          </svg>
                        ) : (
                          <span className={`${typography.caption} font-semibold`}>AI</span>
                        )}
                      </div>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          ) : null}

          {/* Center Preview */}
          <div className="flex-1 h-full min-w-0">
            <div ref={websiteRef} className={`transition-all duration-200 ease-in-out h-full ${isMobile ? (showWebsite ? 'w-full' : 'hidden') : ''}`}>
              <PreviewBackground
                data-tutorial-id="editor-canvas"
                className="h-full flex flex-col overflow-hidden"
                onScroll={handleWebsiteScroll}
              >
                <ZoomControl />
                <div ref={stageHostRef} className="relative flex-1 min-h-0 overflow-hidden">
                  <DeviceStage availableWidth={stageDims.w} availableHeight={stageDims.h}>
                    <DndContext
                      sensors={dragSensors}
                      collisionDetection={collisionDetectionStrategy}
                      onDragStart={handleDndDragStart}
                      onDragCancel={handleDndDragCancel}
                      onDragEnd={handleDndDragEnd}
                    >
                      <motion.div
                        initial={false}
                        animate={{ opacity: hideWebsiteUntilShowcaseComplete ? 0 : 1 }}
                        transition={{
                          duration: hideWebsiteUntilShowcaseComplete ? 0 : 0.35,
                          ease: "easeOut",
                        }}
                        style={{
                          pointerEvents: hideWebsiteUntilShowcaseComplete ? "none" : "auto",
                          visibility: hideWebsiteUntilShowcaseComplete ? "hidden" : "visible",
                        }}
                        aria-hidden={hideWebsiteUntilShowcaseComplete ? "true" : undefined}
                        onDragStartCapture={preventNativeCanvasDragDrop}
                        onDragOverCapture={preventNativeCanvasDragDrop}
                        onDropCapture={preventNativeCanvasDragDrop}
                        onClickCapture={preventCanvasNavigation}
                        onSubmitCapture={preventCanvasNavigation}
                      >
                        <Website version_slug={version_slug} />
                      </motion.div>
                    </DndContext>
                  </DeviceStage>
                  <MovingStatusOverlay label={activeDragMeta?.label} />
                </div>
              </PreviewBackground>
            </div>
          </div>

          {/* Right Dock */}
          {hasRight ? (
            <AnimatePresence initial={false} mode="wait">
              {rightSidebarOpen ? (
                  <motion.div
                    key="dock-right-open"
                    id="dock-right"
                    data-dock="right"
                    data-testid="dock-right"
                    aria-expanded={rightSidebarOpen}
                    className={`h-full ${surfaces.glassCard} border-l ${borders.glass} shadow-lg relative flex flex-col overflow-hidden`}
                    initial={{ width: 0, opacity: 0, x: 8 }}
                  animate={{ width: rightSidebarWidthPx, opacity: 1, x: 0 }}
                  exit={{ width: 0, opacity: 0, x: 8 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  <div
                    data-dock-resizer="right"
                    data-testid="dock-resizer-right"
                    className={`group absolute top-0 left-0 h-full w-6 cursor-col-resize focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${surfaces.hover}`}
                    style={{ zIndex: Z_INDEX.DIVIDER }}
                    onPointerDown={() => startResize('right')}
                    onDoubleClick={() => collapseSide('right')}
                    onKeyDown={(e) => handleOuterResizerKeyDown(e, 'right')}
                    tabIndex={0}
                    aria-label="Resize right dock"
                    aria-controls="dock-right"
                    role="separator"
                    aria-orientation="vertical"
                    title="Resize (Left/Right arrows; Enter to toggle; Double-click to collapse)"
                  >
                    {/* Visual grip */}
                    <div aria-hidden="true" className="absolute inset-y-0 left-0 w-6 flex items-center justify-center">
                      <div className={`h-10 w-1.5 rounded-full bg-gray-300 dark:bg-dark-600 group-hover:bg-gray-400 dark:group-hover:bg-dark-500 shadow-inner`} />
                    </div>
                    {/* Quick expand toggle button — only when below half viewport */}
                    {!isRightExpanded && (
                      <button
                        type="button"
                        data-dock-expand="right"
                        className={`absolute top-1/2 -translate-y-1/2 left-7 -mt-4 w-6 h-6 rounded-full ${surfaces.elevated} border ${borders.default} shadow-sm ${surfaces.hover} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                        title="Expand right panel to half page"
                        aria-label="Expand right panel to half page"
                        onClick={(e) => { e.stopPropagation(); expandSide('right'); }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-4 h-4 ${textColors.tertiary} mx-auto`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14 6l-6 6 6 6" />
                        </svg>
                      </button>
                    )}
                    {/* Quick collapse / shrink toggle button */}
                    <button
                      type="button"
                      data-dock-toggle="right"
                      className={`absolute top-1/2 -translate-y-1/2 left-7 ${isRightExpanded ? '' : 'mt-4'} w-6 h-6 rounded-full ${surfaces.elevated} border ${borders.default} shadow-sm ${surfaces.hover} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                      title={isRightExpanded ? "Shrink right panel" : "Collapse right panel"}
                      aria-label={isRightExpanded ? "Shrink right panel" : "Collapse right panel"}
                      onClick={(e) => { e.stopPropagation(); collapseSide('right'); }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-4 h-4 ${textColors.tertiary} mx-auto`}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 6l6 6-6 6" />
                      </svg>
                    </button>
                  </div>
                  {Array.isArray(rightPanes) ? (
                    <div className="flex-1 min-h-0 flex flex-col">
                      <div className="relative" style={{ height: `${rightSplitRatio * 100}%` }}>
                        {renderPaneForSide(rightPanes[0], 'right')}
                      </div>
                      <div
                        data-dock-inner-resizer="right"
                        data-testid="dock-inner-resizer-right"
                        className={`relative h-6 bg-transparent ${surfaces.hover} cursor-ns-resize flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`}
                        style={{ zIndex: Z_INDEX.DIVIDER }}
                        onPointerDown={() => startInnerSplitResize('right')}
                        onKeyDown={(e) => handleInnerResizerKeyDown(e, 'right')}
                        tabIndex={0}
                        aria-label="Resize right dock panes"
                        role="separator"
                        aria-orientation="horizontal"
                        aria-controls="dock-right"
                        title="Resize panes (Up/Down arrows; Shift for larger step)"
                      >
                        <div aria-hidden="true" className={`absolute inset-x-0 top-1/2 -translate-y-1/2 h-px ${borders.strong}`} />
                      </div>
                      <div className="flex-1 min-h-0">
                        {renderPaneForSide(rightPanes[1], 'right')}
                      </div>
                    </div>
                  ) : (
                    renderPaneForSide(rightPanes, 'right')
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="dock-right-closed"
                  initial={{ opacity: 0, x: 8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="pointer-events-none"
                >
                  <div
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2"
                    style={{ zIndex: Z_INDEX.DIVIDER }}
                  >
                    <button
                      data-dock-handle="right"
                      data-testid="dock-handle-right-open"
                      type="button"
                      className={`pointer-events-auto w-12 h-12 rounded-full ${surfaces.elevated} border ${borders.default} shadow ${surfaces.hover} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex items-center justify-center group`}
                      title="Open panel"
                      aria-label="Open panel"
                      onClick={() => setRightSidebarOpen(true)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setRightSidebarOpen(true); e.preventDefault(); } }}
                    >
                      <div className={`relative w-full h-full flex items-center justify-center ${textColors.secondary}`}>
                        {rightPrimaryKind === 'edit' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.121 2.121 0 113 3L8.5 17.85 4 19l1.15-4.5L16.862 3.487z" />
                          </svg>
                        ) : (
                          <span className={`${typography.caption} font-semibold`}>AI</span>
                        )}
                      </div>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          ) : null}
            </>
          )}
        </div>
      </div>
    </AddElementProvider>
  );
});

EditWebsiteScaffold.displayName = "EditWebsiteScaffold";

const EditWebsitePage = () => {
  useDevRenderLoopGuard("EditWebsitePage");
  const location = useLocation();
  const { username, domain, version_slug, subdomain } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [showWebsite, setShowWebsite] = useState(true);
  const [activeDragMeta, setActiveDragMeta] = useState(null);
  const [creationShowcaseActive, setCreationShowcaseActive] = useState(false);
  const [hideWebsiteUntilShowcaseComplete, setHideWebsiteUntilShowcaseComplete] = useState(false);
  const creationShowcaseHasRunRef = useRef(false);
  const [pages, setPages] = useState([]);

  const canonicalizeSubdomain = useCallback((raw) => {
    const s = (raw ?? "").toString().trim();
    if (!s) return "";
    return s.replace(/^\/+/, "").replace(/\/+$/, "");
  }, []);

  const currentPage = useMemo(() => {
    const pageList = Array.isArray(pages) ? pages : [];
    if (pageList.length === 0) return null;

    // Route param is optional (`:subdomain?`). If it's missing, the editor still renders a page
    // (typically the first page returned by the backend). Use that as our "currently viewed page"
    // for AI context and other page-scoped features.
    const wanted = canonicalizeSubdomain(subdomain);
    if (!wanted) return pageList[0];

    return (
      pageList.find((p) => canonicalizeSubdomain(p?.subdomain).toLowerCase() === wanted.toLowerCase()) ||
      pageList[0]
    );
  }, [pages, subdomain, canonicalizeSubdomain]);

  // If the URL subdomain does not exist (common when a user types a URL or a link points to a removed page),
  // redirect (replace) to the best fallback page so the renderer doesn't endlessly refetch 404s.
  useEffect(() => {
    const pageList = Array.isArray(pages) ? pages : [];
    if (pageList.length === 0) return;
    const wanted = canonicalizeSubdomain(subdomain);
    if (!wanted) return; // already on main page route

    const match = pageList.find(
      (p) => canonicalizeSubdomain(p?.subdomain).toLowerCase() === wanted.toLowerCase(),
    );
    if (match) return;

    const fallback = pageList[0];
    const nextUrl = generateEditUrl(username, domain, version_slug, fallback?.subdomain || "");
    if (nextUrl && nextUrl !== window.location.pathname) {
      navigate(nextUrl, { replace: true });
    }
  }, [pages, subdomain, canonicalizeSubdomain, username, domain, version_slug, navigate]);
  const [showPublishToast, setShowPublishToast] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [expiringPlanModalOpen, setExpiringPlanModalOpen] = useState(false);
  const [expiringPlanInfo, setExpiringPlanInfo] = useState(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradingFromExpiringPlan, setUpgradingFromExpiringPlan] = useState(false);

  // Handle scroll events to prevent propagation between sections
  const handleWebsiteScroll = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleEditPanelScroll = useCallback((e) => {
    e.stopPropagation();
  }, []);

  useEffect(() => {
    // Show success modal after redirect from publish
    try {
      if (sessionStorage.getItem('justPublished') === 'true') {
        const expiringPlanStr = sessionStorage.getItem('expiringPlan');
        if (expiringPlanStr) {
          setExpiringPlanInfo(JSON.parse(expiringPlanStr));
          setExpiringPlanModalOpen(true);
          sessionStorage.removeItem('expiringPlan');
        } else {
          setShowPublishToast(true);
          setShowConfetti(true);
        }
        sessionStorage.removeItem('justPublished');
        // Modal stays open until manually dismissed - no auto-dismiss
      }
    } catch (_) {}
  }, []);

  const handleKeepCurrentPlan = () => {
    setExpiringPlanModalOpen(false);
    setShowPublishToast(true);
    setShowConfetti(true);
  };

  const getExpirationText = () => {
    if (!expiringPlanInfo) return "";
    const dateStr = expiringPlanInfo.expirationDate
      ? new Date(expiringPlanInfo.expirationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      : "soon";

    const planName = expiringPlanInfo.planDisplayName || "Plus plan";
    const subscriptionsDisabled = Boolean(expiringPlanInfo.subscriptionsDisabled);

    if (expiringPlanInfo.isAccessGrant) {
      if (subscriptionsDisabled) {
        return `You have the ${planName} for free until ${dateStr} via an access grant. Your website will stay live until then. We're working on paid plans and will let you know as soon as they're available.`;
      }
      return `You have the ${planName} for free until ${dateStr} via an access grant. Your website will stay live until then, but not after. Upgrade to keep it published!`;
    }

    if (subscriptionsDisabled) {
      return `Your ${planName} ends on ${dateStr}. Your website will stay live until then. We're working on paid plans and will let you know as soon as they're available.`;
    }
    return `Your ${planName} is expiring on ${dateStr}. Your website will stay live until then, but not after. Upgrade to keep it published!`;
  };

  const isExpiringPlanWithSubscriptionsDisabled = Boolean(expiringPlanInfo?.subscriptionsDisabled);
  const expiringPlanModalTitle = isExpiringPlanWithSubscriptionsDisabled
    ? "Published! A note about your plan"
    : "Published! But your plan is expiring...";

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Showcase on first editor entry after creating a website (query flag set by CreateNewWebsite).
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const shouldPrompt = params.get("tutorial_prompt") === "1";
    if (!shouldPrompt) {
      // Defensive reset if user navigates into the editor without the creation flag.
      creationShowcaseHasRunRef.current = false;
      setCreationShowcaseActive(false);
      setHideWebsiteUntilShowcaseComplete(false);
      return undefined;
    }

    // Prevent re-running due to subsequent data loads (e.g. website id resolving) or route churn.
    if (creationShowcaseHasRunRef.current) return undefined;

    const fallbackKey = `editor:tutorialPromptDismissed:edit:${username}:${domain}:${version_slug}`;

    try {
      const dismissed = window.localStorage.getItem(fallbackKey) === "1";
      if (dismissed) return undefined;
    } catch (_) {
      // If localStorage is unavailable, still run the showcase once.
    }

    creationShowcaseHasRunRef.current = true;
    setHideWebsiteUntilShowcaseComplete(true);
    setCreationShowcaseActive(true);
    const tActive = window.setTimeout(
      () => setCreationShowcaseActive(false),
      WEBSITE_CREATION_SHOWCASE_DURATION_MS,
    );
    const tReveal = window.setTimeout(
      () => setHideWebsiteUntilShowcaseComplete(false),
      WEBSITE_CREATION_SHOWCASE_TOTAL_MS,
    );
    return () => {
      // In React 18 StrictMode (dev), effects mount → cleanup → mount to help surface side-effects.
      // Clearing timers here is correct as long as we reset the guard so the next run can schedule them again.
      try {
        window.clearTimeout(tActive);
        window.clearTimeout(tReveal);
      } catch (_) {}
      creationShowcaseHasRunRef.current = false;
    };
  }, [domain, location.search, username, version_slug]);

  const refreshWebsite = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/websites/domain/${username}/${domain}/${version_slug}/`);
      if (!res.ok) {
        if (res.status === 404) {
          setNotFound(true);
          return;
        }
        const data = await res.json();
        setError(data.error || "Website not found");
        return;
      }
      const data = await res.json();
      setWebsite(data);
    } catch {
      setError("Network error");
    }
  }, [username, domain, version_slug]);

  const refreshPages = useCallback(async () => {
    if (!website?.id) return;
    try {
      const res = await apiFetch(`/api/websites/${website.id}/pages/`);
      if (!res.ok) return;
      const data = await res.json();
      // Normalize to array if needed
      setPages(Array.isArray(data) ? data : [data]);
    } catch {
      // ignore
    }
  }, [website?.id]);

  useEffect(() => {
    refreshWebsite();
  }, [refreshWebsite]);

  // Fetch all pages for the website for the navbar
  useEffect(() => {
    refreshPages();
  }, [refreshPages]);

  // Allows targeted refresh without a full page reload (used by AI incremental reload targets)
  useEffect(() => {
    const handler = () => {
      refreshWebsite();
      refreshPages();
    };

    window.addEventListener("editor:refresh-website", handler);
    return () => window.removeEventListener("editor:refresh-website", handler);
  }, [refreshWebsite, refreshPages]);

  // Guard: redirect to dashboard when the website is still being built by AI.
  // This prevents users from editing a half-generated website by navigating
  // directly to the edit URL or via bookmarks.
  useEffect(() => {
    const aiStatus = website?.ai_generation_status;
    if (aiStatus === "queued" || aiStatus === "running") {
      navigate("/admin", { replace: true });
    }
  }, [website?.ai_generation_status, navigate]);

  // Handler to dismiss publish success modal (must be defined before conditional returns)
  const handleDismissPublishModal = useCallback(() => {
    setShowPublishToast(false);
    setShowConfetti(false);
  }, []);

  // [Removed legacy percent-based split drag handlers and old modal system]
  // Element creation is now handled by AddElementPanel in the sidebar/dock

  const AddButtonsBetweenComponents = ({ components, pageId }) => {
    const { openAddModal } = useAddElement();
    if (!components || components.length === 0) {
      return [
        <AddButtonOverlay
          key="page-empty-overlay"
          layer="page-empty"
          overlayId={`page-empty-${pageId}`}
          variant="page"
          strategy="inline"
          className="flex justify-center my-4"
        >
          <AddButtonWithIndicator
            tutorialTargetId={TUTORIAL_TARGETS.ADD_BUTTON}
            onClick={(e) => {
              console.log('🔍 ADDBUTTON_ORDER_BUG - Page-level AddButton clicked (empty page):', {
                pageId,
                websiteId: website.id,
                order: 0,
              });
              openAddModal(
                {
                  pageId,
                  websiteId: website.id,
                  order: 0,
                },
                e,
              );
            }}
          />
        </AddButtonOverlay>,
      ];
    }

    const parts = [];
    components.forEach((comp, idx) => {
      if (idx === 0) {
        parts.push(
          <AddButtonOverlay
            key={`btn-before-${comp.id}`}
            layer="page-linear-before"
            overlayId={`edit-page-linear-before-${pageId}-${comp.id}`}
            variant="page"
            strategy="inline"
            className="flex justify-center my-4"
          >
            <AddButtonWithIndicator
              tutorialTargetId={TUTORIAL_TARGETS.ADD_BUTTON}
              onClick={(e) => {
                console.log('🔍 ADDBUTTON_ORDER_BUG - Page-level AddButton clicked (before first):', {
                  pageId,
                  websiteId: website.id,
                  order: 0,
                });
                openAddModal(
                  {
                    pageId,
                    websiteId: website.id,
                    order: 0,
                  },
                  e,
                );
              }}
            />
          </AddButtonOverlay>,
        );
      }

      parts.push(
        <div key={comp.id} className="m-0 p-0 w-full">
          <Component componentId={comp.id} />
        </div>,
      );

      parts.push(
        <AddButtonOverlay
          key={`btn-after-${comp.id}`}
          layer="page-linear-after"
          overlayId={`edit-page-linear-after-${pageId}-${comp.id}-${idx}`}
          variant="page"
          shift={idx === components.length - 1 ? "right" : "none"}
          strategy="inline"
          className="flex justify-center my-4"
        >
          <AddButtonWithIndicator
            onClick={(e) => {
              console.log('🔍 ADDBUTTON_ORDER_BUG - Page-level AddButton clicked (after component):', {
                pageId,
                websiteId: website.id,
                order: idx + 1,
                afterComponentId: comp.id,
              });
              openAddModal(
                {
                  pageId,
                  websiteId: website.id,
                  order: idx + 1,
                },
                e,
              );
            }}
          />
        </AddButtonOverlay>,
      );
    });
    return parts;
  };

  // ============================================
  // ALL HOOKS MUST BE CALLED BEFORE ANY RETURNS
  // ============================================

  // Sensor: require the pointer to move at least 8px before drag activates.
  // This keeps click-to-select working when the user taps without moving.
  // On mobile, prefer a short press delay so scroll gestures don't accidentally trigger drag.
  const dragActivationConstraint = useMemo(
    () => (isMobile ? { delay: 200, tolerance: 8 } : { distance: 8 }),
    [isMobile],
  );
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: dragActivationConstraint })
  );

  const onDragStart = useCallback((event) => {
    const activeId = String(event?.active?.id || "");
    if (!activeId) return;
    const parsed = parseSortableId(activeId);
    const activeData = event?.active?.data?.current || {};
    const type = parsed?.type || activeData?.itemType || activeData?.kind || "item";
    const label = String(type).replace(/_/g, " ");
    setActiveDragMeta({
      id: activeId,
      label: label.charAt(0).toUpperCase() + label.slice(1),
    });
  }, []);

  // Define onDragEnd handler with useCallback (must be before any conditional returns)
  const onDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveDragMeta(null);
      return;
    }

    const activeId = String(active.id);
    const overId = String(over.id);
    const isParentTarget = overId.startsWith("parent:");

    // dnd-kit data supplied by Sortable wrappers
    const activeData = active.data?.current || {};
    const overData = over.data?.current || {};

    const activeMeta = parseSortableId(activeId);

    // ---- Undo history: capture "from" state before any move ----
    const moveFrom = {
      parentType: activeData.parentType,
      parentId: activeData.parentId,
      parentComponentId: activeData.parentComponentId || null,
      order: typeof activeData.index === "number" ? activeData.index : null,
    };
    const dispatchRecordMove = (to) => {
      window.dispatchEvent(new CustomEvent("editor:record-move", {
        detail: {
          entityType: activeMeta.type === "component" ? "component" : "item",
          entityId: activeMeta.id,
          itemType: activeMeta.type !== "component" ? activeMeta.type : null,
          from: moveFrom,
          to,
          websiteId: website?.id,
        },
      }));
    };

    try {
      if (isParentTarget) {
        const [, parentScope, parentRawId] = overId.split(":");
        if (parentScope === "page") {
          if (activeMeta.type !== "component") return;
          const pageId = Number(parentRawId);
          await moveComponentApi({ id: activeMeta.id, toParentWebsitePageId: pageId, order: null });
        } else if (parentScope === "component") {
          const targetParentId = Number(parentRawId);
          if (activeMeta.type === "component") {
            return;
          } else {
            await moveItemApi({ type: activeMeta.type, id: activeMeta.id, toComponentId: targetParentId, order: null });
          }
        } else if (parentScope === "hero") {
          const targetHeroId = Number(parentRawId);
          // Components can't be nested inside a hero — only items can.
          if (activeMeta.type === "component") {
            return;
          }
          await moveItemApi({ type: activeMeta.type, id: activeMeta.id, toParentHeroId: targetHeroId, order: null });
        }
        // Invalidate source parent if moving from a hero
        if (activeData.parentType === "hero" && activeData.parentId) {
          queryClient.invalidateQueries({ queryKey: ["item", "hero", activeData.parentId] });
          if (activeData.parentComponentId) {
            queryClient.invalidateQueries({ queryKey: ["component", activeData.parentComponentId] });
          }
        } else if (activeData.parentType === "component" && activeData.parentId) {
          queryClient.invalidateQueries({ queryKey: ["component", activeData.parentId] });
        }
        // Invalidate target parents to reflect changes
        if (overId.includes(":page:")) {
          // page skeleton
          queryClient.invalidateQueries({ queryKey: ["page-skeleton", website?.id] });
        } else if (overId.includes(":component:")) {
          const cid = Number(overId.split(":")[2]);
          queryClient.invalidateQueries({ queryKey: ["component", cid] });
        } else if (overId.includes(":hero:")) {
          const hid = Number(overId.split(":")[2]);
          // Refresh the hero's children, and the owning component so the
          // hero re-renders inside it with the newly nested item.
          queryClient.invalidateQueries({ queryKey: ["item", "hero", hid] });
          if (overData.parentComponentId) {
            queryClient.invalidateQueries({ queryKey: ["component", overData.parentComponentId] });
          }
        }
        // Record for undo so Ctrl+Z can reverse this cross-parent move
        if (parentScope === "page" || parentScope === "component") {
          dispatchRecordMove({
            parentType: parentScope,
            parentId: Number(parentRawId),
            order: null,
          });
        } else if (parentScope === "hero") {
          dispatchRecordMove({
            parentType: "hero",
            parentId: Number(parentRawId),
            parentComponentId: overData.parentComponentId || null,
            order: null,
          });
        }
        return;
      }

      // Reorder within or across parents when dropping over a sibling
      // Use overData to determine target parent and index
      const targetParentType = overData.parentType;
      const targetParentId = overData.parentId;
      const targetIndex = typeof overData.index === "number" ? overData.index : null;

      if (!targetParentType || !targetParentId || targetIndex == null) return;

      if (activeMeta.type === "component") {
        // Move/reorder component
        if (targetParentType === "page") {
          await moveComponentApi({ id: activeMeta.id, toParentWebsitePageId: targetParentId, order: targetIndex });
          queryClient.invalidateQueries({ queryKey: ["page-skeleton", website?.id] });
        } else if (targetParentType === "component") {
          return;
        }
      } else {
        // Move/reorder item
        if (targetParentType === "component") {
          if (
            activeData.autoLayout &&
            overData.autoLayout &&
            activeData.parentId === targetParentId &&
            activeData.autoBucket &&
            overData.autoBucket &&
            activeData.autoBucket !== overData.autoBucket
          ) {
            return;
          }
          await moveItemApi({ type: activeMeta.type, id: activeMeta.id, toComponentId: targetParentId, order: targetIndex });
          queryClient.invalidateQueries({ queryKey: ["component", targetParentId] });
          // Invalidate source parent if different
          if (activeData.parentType === "hero" && activeData.parentId) {
            queryClient.invalidateQueries({ queryKey: ["item", "hero", activeData.parentId] });
            if (activeData.parentComponentId) {
              queryClient.invalidateQueries({ queryKey: ["component", activeData.parentComponentId] });
            }
          } else if (activeData.parentType === "component" && activeData.parentId !== targetParentId) {
            queryClient.invalidateQueries({ queryKey: ["component", activeData.parentId] });
          }
        } else if (targetParentType === "hero") {
          await moveItemApi({ type: activeMeta.type, id: activeMeta.id, toParentHeroId: targetParentId, order: targetIndex });
          // Invalidate the hero item query to refresh its children immediately
          queryClient.invalidateQueries({ queryKey: ["item", "hero", targetParentId] });
          // Also invalidate the owning component query
          queryClient.invalidateQueries({ queryKey: ["component", overData.parentComponentId] });
          // Invalidate source parent if different from target
          if (activeData.parentType === "hero" && activeData.parentId !== targetParentId) {
            queryClient.invalidateQueries({ queryKey: ["item", "hero", activeData.parentId] });
            if (activeData.parentComponentId && activeData.parentComponentId !== overData.parentComponentId) {
              queryClient.invalidateQueries({ queryKey: ["component", activeData.parentComponentId] });
            }
          } else if (activeData.parentType === "component" && activeData.parentId) {
            queryClient.invalidateQueries({ queryKey: ["component", activeData.parentId] });
          }
        } else {
          return;
        }
      }
      // Record successful sibling-target move for undo
      const didMove = activeMeta.type === "component"
        ? (targetParentType === "page" || targetParentType === "component")
        : (targetParentType === "component" || targetParentType === "hero");
      if (didMove) {
        dispatchRecordMove({
          parentType: targetParentType,
          parentId: targetParentId,
          parentComponentId: targetParentType === "hero" ? (overData.parentComponentId || null) : null,
          order: targetIndex,
        });
      }
    } catch (e) {
      console.error("Error during drag-and-drop:", e);
      // As a fallback, invalidate both involved parents
      if (activeData.parentType === "component") {
        queryClient.invalidateQueries({ queryKey: ["component", activeData.parentId] });
      } else if (activeData.parentType === "hero") {
        queryClient.invalidateQueries({ queryKey: ["item", "hero", activeData.parentId] });
        if (activeData.parentComponentId) {
          queryClient.invalidateQueries({ queryKey: ["component", activeData.parentComponentId] });
        }
      }
      if (overData.parentType === "component") {
        queryClient.invalidateQueries({ queryKey: ["component", overData.parentId] });
      } else if (overData.parentType === "hero") {
        queryClient.invalidateQueries({ queryKey: ["item", "hero", overData.parentId] });
        if (overData.parentComponentId) {
          queryClient.invalidateQueries({ queryKey: ["component", overData.parentComponentId] });
        }
      }
    } finally {
      setActiveDragMeta(null);
    }
  }, [queryClient, website?.id]);

  // Hard-stop browser-native drag/drop and navigation defaults inside the editor canvas.
  // dnd-kit uses pointer events, so blocking native HTML5 DnD here is safe and prevents
  // accidental page reloads from dropped links/text/images.
  const preventNativeCanvasDragDrop = useCallback((event) => {
    event.preventDefault();
  }, []);

  // In edit mode, links/forms rendered in the canvas should never trigger real navigation.
  // Keep bubbling intact so element selection/edit interactions still work.
  const preventCanvasNavigation = useCallback((event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("a[href],form")) {
      event.preventDefault();
    }
  }, []);

  // ============================================
  // NOW WE CAN DO CONDITIONAL RETURNS
  // ============================================
  
  if (notFound) {
    return (
      <div className={`editor-theme min-h-screen ${surfaces.modernApp} flex flex-col`}>
        <NotFoundWebsite username={username} domain={domain} />
      </div>
    );
  }
  if (error) {
    return (
      <div className={`editor-theme min-h-screen ${surfaces.modernApp} flex flex-col`}>
        <ErrorWebsite username={username} domain={domain} error={error} />
      </div>
    );
  }
  if (!website) {
    return (
      <div className={`editor-theme min-h-screen ${surfaces.modernApp} flex flex-col`}>
        <LoadingPage />
      </div>
    );
  }

  // Toggle handler for mobile
  const toggleView = () => setShowWebsite((prev) => !prev);

  return (
    <div className={`editor-theme min-h-screen h-full w-full flex flex-col ${surfaces.modernApp}`}>
      <EditProvider
        websiteId={website?.id}
        pageId={currentPage?.id || null}
        pageSubdomain={currentPage?.subdomain || subdomain || null}
      >
        <ExpandedFieldProvider>
        <AIProvider>
        <PreviewProvider>
          <AddButtonLayerProvider>
            <EditUIProvider>
              <EditorTutorialProvider>
                <EditNavBar
                  username={username}
                  domain={domain}
                  version_slug={version_slug}
                  pages={pages}
                  websiteId={website?.id}
                  website={website}
                />
                <EditorInteractionProvider>
                  <EditWebsiteScaffold
                    isMobile={isMobile}
                    showWebsite={showWebsite}
                    setShowWebsite={setShowWebsite}
                    toggleView={toggleView}
                    website={website}
                    currentPage={currentPage}
                    handleWebsiteScroll={handleWebsiteScroll}
                    handleEditPanelScroll={handleEditPanelScroll}
                    hideWebsiteUntilShowcaseComplete={hideWebsiteUntilShowcaseComplete}
                    dragSensors={dragSensors}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    activeDragMeta={activeDragMeta}
                    setActiveDragMeta={setActiveDragMeta}
                    preventNativeCanvasDragDrop={preventNativeCanvasDragDrop}
                    preventCanvasNavigation={preventCanvasNavigation}
                    version_slug={version_slug}
                  />
                </EditorInteractionProvider>
                <WebsiteCreationShowcase isActive={creationShowcaseActive} />
                <EditorTutorialOverlay />
                {/* OLD: AI Chat Panel (floating) - now integrated into EditPanel */}
                {/* <AIChatPanel /> */}
              </EditorTutorialProvider>
            </EditUIProvider>
          </AddButtonLayerProvider>
        </PreviewProvider>
        {/* Confetti celebration */}
        <ConfettiOverlay isActive={showConfetti} />
        
        {/* Upgrade Plan Modal */}
        <SubscriptionPlansModal
          isOpen={upgradeModalOpen}
          onClose={() => {
            setUpgradeModalOpen(false);
            if (upgradingFromExpiringPlan) {
              setExpiringPlanModalOpen(true);
              setUpgradingFromExpiringPlan(false);
            }
          }}
        />

        {/* Expiring Plan / Access Grant warning modal before normal published modal */}
        <Modal
          isOpen={expiringPlanModalOpen}
          onClose={handleKeepCurrentPlan}
          title={expiringPlanModalTitle}
          size="md"
          closeOnOverlayClick={false}
          closeOnEsc={false}
          showCloseButton={false}
        >
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-green-400 text-white flex items-center justify-center">
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <p className={`${typography.body} mb-6`}>{getExpirationText()}</p>
            <div className="flex flex-col space-y-3 mt-6">
              {isExpiringPlanWithSubscriptionsDisabled ? (
                <PrimaryButton fullWidth onClick={handleKeepCurrentPlan}>
                  OK
                </PrimaryButton>
              ) : (
                <>
                  <PrimaryButton
                    fullWidth
                    onClick={() => {
                      setExpiringPlanModalOpen(false);
                      setUpgradingFromExpiringPlan(true);
                      setUpgradeModalOpen(true);
                    }}
                  >
                    Upgrade plan
                  </PrimaryButton>
                  <SecondaryButton
                    fullWidth
                    onClick={handleKeepCurrentPlan}
                  >
                    Keep current plan
                  </SecondaryButton>
                </>
              )}
            </div>
          </div>
        </Modal>

        {/* Success modal after publish */}
        <Modal
          isOpen={showPublishToast}
          onClose={handleDismissPublishModal}
          title="Published!"
          size="md"
          closeOnOverlayClick={true}
          closeOnEsc={true}
          showCloseButton={true}
        >
          <div className="text-center space-y-4">
            {/* Success icon */}
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-green-400 text-white flex items-center justify-center">
                <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            
            {/* Success message with URL */}
            <div className="space-y-3">
              <p className={typography.body}>
                "<span className="font-semibold">{APP_DOMAIN}/{username}/{domain || website?.domain}</span>" is now live and updated!
              </p>
              
              {/* Action buttons */}
              <div className="space-y-2">
                <PrimaryButton
                  fullWidth
                  leftIcon={<ExternalLinkIcon size="sm" />}
                  className="flex-wrap"
                  onClick={() => {
                    const publishedUrl = `https://www.${APP_DOMAIN}/${username}/${domain || website?.domain}`;
                    const newWindow = window.open(publishedUrl, "_blank", "noopener,noreferrer");
                    if (newWindow) newWindow.opener = null;
                  }}
                >
                  <span className="whitespace-normal break-words">
                    Access the website:{" "}
                    <span className="font-semibold">
                      {APP_DOMAIN}/{username}/{domain || website?.domain}
                    </span>
                  </span>
                </PrimaryButton>

                <SecondaryButton
                  onClick={handleDismissPublishModal}
                  className="w-full"
                >
                  Continue editing
                </SecondaryButton>
              </div>
            </div>
          </div>
        </Modal>
      </AIProvider>
      </ExpandedFieldProvider>
    </EditProvider>
    </div>
  );
};

export default EditWebsitePage;
